import { InsightsDateRange, InsightsDateRangeLabels, ResolvedFilter, useFilters } from '@percent/domain/insights'
import { matchDatePreset, resolveDatePreset } from './datePresets'

type DateBoundsFilterProps = {
  def: ResolvedFilter
  fromKey: string
  toKey: string
  labels: InsightsDateRangeLabels
  testId: string
}

/**
 * A date control over a pair of `<x>From` / `<x>To` filters. The platform's date-range control
 * drives the single `dateRange` global, so a report with a second date dimension writes the
 * bounds itself — rendered for the `From` filter, which carries both. It offers the `dateRange`
 * presets and writes each one as the window the API resolves for it.
 */
export function DateBoundsFilter({ def, fromKey, toKey, labels, testId }: Readonly<DateBoundsFilterProps>) {
  const { values, setValues, filterDefs } = useFilters()
  const from = values[fromKey] as string | undefined
  const to = values[toKey] as string | undefined

  const presets = (filterDefs.find(filter => filter.valueType === 'dateRange')?.presets ?? []).filter(preset =>
    resolveDatePreset(preset.key)
  )
  const period = matchDatePreset(
    presets.map(preset => preset.key),
    { from, to }
  )

  return (
    <InsightsDateRange
      def={{ ...def, presets }}
      period={period}
      from={period ? undefined : from}
      to={period ? undefined : to}
      onChange={value => {
        const window = value && 'period' in value ? resolveDatePreset(value.period) : value

        setValues({ [fromKey]: window?.from, [toKey]: window?.to })
      }}
      labels={labels}
      dropdownAlign="start"
      labelWhenAllTime
      testId={testId}
    />
  )
}
